import { QueryParams } from '@/constants/QueryParam';
import { parseFeatureFlagsQueryParam } from '@/featureFlags/parseFeatureFlagsQueryParam';
import {
  type FeatureFlagId,
  featureFlags,
  type FeatureFlagValue,
  getFeatureFlagConfig,
} from '@/featureFlags/registry';
import useRemoteFeatureFlag from '@/featureFlags/useRemoteFeatureFlag';
import { useQueryParam } from '@/hooks/useQueryParam/useQueryParam';
import { useFeatureFlagOverridesStore } from '@/store/featureFlagOverridesStore';
import { environment } from '@/utils/env.utils';

const useFeatureFlag = <T extends FeatureFlagId>(flagId: T): FeatureFlagValue<T> => {
  const config = featureFlags[flagId];
  const remoteValue = useRemoteFeatureFlag(config);
  const storeOverride = useFeatureFlagOverridesStore(state => state.overrides[config.key]);
  const featureFlagsParam = useQueryParam(QueryParams.FEATURE_FLAGS);

  if (environment.env === 'production') {
    return remoteValue;
  }

  const urlOverrides = parseFeatureFlagsQueryParam(featureFlagsParam);
  const override = config.key in urlOverrides ? urlOverrides[config.key] : storeOverride;

  if (override === undefined) {
    return remoteValue;
  }

  if (config.type === 'boolean' && typeof override === 'boolean') {
    return override as FeatureFlagValue<T>;
  }

  const flagConfig = getFeatureFlagConfig(flagId);
  if (
    flagConfig.type === 'multivariate' &&
    typeof override === 'string' &&
    (flagConfig.variants as readonly string[]).includes(override)
  ) {
    return override as FeatureFlagValue<T>;
  }

  return remoteValue;
};

export default useFeatureFlag;
